import React, { useEffect } from 'react';
import { Link } from "react-router-dom";
import { Helmet } from 'react-helmet';
import Navbar from './Navbar';
import Footer from './Footer';
import { TbUsers, TbBuildingFactory, TbFileInvoice, TbShoppingCart, TbChartLine, TbBattery, TbPlug, TbShield } from 'react-icons/tb';

function UtilityScaleSolar() {
  // Scroll to top when component mounts
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);


  return (
    <>
      <Helmet>
        <title>Enterprise Power Solutions | Group Captive, VPPA, PPA Models | Lunar Electric</title>
        <meta name="description" content="Enterprise Power Solutions: Group Captive, Fully Captive, Hybrid, BESS, VPPA, and Group PPA models for enterprise energy security. Renewable energy procurement for industrial and commercial facilities." />
        <meta name="keywords" content="enterprise power solutions, group captive PPA, fully captive solar, VPPA virtual power purchase agreement, group PPA, BESS battery energy storage, enterprise solar, industrial solar, commercial solar, energy security, renewable energy procurement" />
        <meta name="robots" content="index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1" />
        <meta property="og:title" content="Enterprise Power Solutions | Group Captive, VPPA, PPA Models | Lunar Electric" />
        <meta property="og:description" content="Enterprise Power Solutions: Group Captive, Fully Captive, Hybrid, BESS, VPPA, and Group PPA models for enterprise energy security." />
        <meta property="og:url" content="https://lunarelectric.in/utility-scale-solar" />
        <meta name="twitter:title" content="Enterprise Power Solutions | Group Captive, VPPA, PPA Models | Lunar Electric" />
        <meta name="twitter:description" content="Enterprise Power Solutions: Group Captive, Fully Captive, Hybrid, BESS, VPPA, and Group PPA models for enterprise energy security." />
        <link rel="canonical" href="https://lunarelectric.in/utility-scale-solar" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Service",
          "serviceType": "Enterprise Power Solutions - Group Captive, VPPA, PPA Models",
          "provider": {
            "@type": "Organization",
            "name": "Lunar Electric",
            "url": "https://lunarelectric.in"
          },
          "areaServed": {
            "@type": "Country",
            "name": "India"
          },
          "description": "Enterprise Power Solutions: Group Captive, Fully Captive, Hybrid, BESS, VPPA, and Group PPA models for enterprise energy security and renewable energy procurement",
          "offers": {
            "@type": "Offer",
            "priceRange": "Project-based pricing",
            "priceCurrency": "INR"
          }
        })}</script>
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BreadcrumbList",
          "itemListElement": [
            {
              "@type": "ListItem",
              "position": 1,
              "name": "Home",
              "item": "https://lunarelectric.in/"
            },
            {
              "@type": "ListItem",
              "position": 2,
              "name": "Enterprise Power Solutions",
              "item": "https://lunarelectric.in/utility-scale-solar"
            }
          ]
        })}</script>
      </Helmet>
      <a href="#main-content" className="skip-to-main" style={{position: 'absolute', left: '-9999px', zIndex: 9999}} onFocus={(e) => e.target.style.left = '0'}>Skip to main content</a>
      <div className="utility-solar-page" id="main-content">
      <Navbar />
      
      {/* Hero Section */}
      <section className="utility-hero">
        <div className="container">
          <div className="hero-content">
            <h1>Enterprise Power Solutions</h1>
            <p className="hero-subtitle">Group Captive, Fully Captive, Hybrid, BESS, VPPA, and Group PPA models for enterprise energy security</p>
            <p className="hero-description">
              Four procurement models: Group Captive (shared ownership), Fully Captive (on-site generation), 
              VPPA (off-site contracts), and Group PPA (volume aggregation). Each offers different ownership structures, 
              tariff arrangements, and regulatory compliance paths.
            </p>
            <div className="hero-buttons">
              <Link to="/callback" className="btn-primary">
                Discuss Your Project
              </Link>
              <a href="tel:+919148857417" className="btn-secondary">
                Call Now
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Enterprise Models Section */}
      <section className="utility-services">
        <div className="container">
          <h2>Enterprise Power Procurement Models</h2>
          <p className="section-intro" style={{ textAlign: 'center', marginBottom: '3rem', fontSize: '18px', color: '#666', maxWidth: '900px', margin: '0 auto 3rem' }}>
            Four procurement models to match your facility type, consumption pattern, and capital structure. Each model offers different ownership structures, tariff arrangements, and regulatory benefits.
          </p>
          <div className="services-grid">
            <div className="service-card">
              <TbUsers className="specialization-icon" />
              <h3>Group Captive PPA</h3>
              <p style={{ textAlign: 'center' }}>Multiple entities share a renewable energy plant and consume power through open access. Each participant gets their allocated capacity and can use excess generation. Lowers per-MW investment and improves project bankability through consortium structure.</p>
              <ul style={{ textAlign: 'left', marginTop: '1rem', paddingLeft: '1.5rem' }}>
                <li>Shared plant ownership reduces upfront capital</li>
                <li>Open access power consumption at competitive tariffs</li>
                <li>Better for industrial parks and manufacturing clusters</li>
              </ul>
            </div>
            <div className="service-card">
              <TbBuildingFactory className="specialization-icon" />
              <h3>Fully Captive Model</h3>
              <p style={{ textAlign: 'center' }}>Single-entity owns and consumes 100% of the renewable energy plant output. All generated power is used on-site or banked with the grid. Full control over operations, no shared ownership complications, and maximum renewable energy credit benefits.</p>
              <ul style={{ textAlign: 'left', marginTop: '1rem', paddingLeft: '1.5rem' }}>
                <li>100% ownership and consumption of plant output</li>
                <li>On-site generation with grid banking facility</li>
                <li>Best for data centers, large manufacturing, cold storage</li>
              </ul>
            </div>
            <div className="service-card">
              <TbFileInvoice className="specialization-icon" />
              <h3>VPPA (Virtual Power Purchase Agreement)</h3>
              <p style={{ textAlign: 'center' }}>Financial contract for off-site renewable energy procurement without physical connection. You pay a fixed price per unit while the developer sells power to the grid. Provides price stability, counts toward RE100 goals, and requires no roof or land.</p>
              <ul style={{ textAlign: 'left', marginTop: '1rem', paddingLeft: '1.5rem' }}>
                <li>Fixed-price hedge against grid tariff volatility</li>
                <li>RE100 and ESG reporting compliance</li>
                <li>No physical infrastructure needed</li>
              </ul>
            </div>
            <div className="service-card">
              <TbShoppingCart className="specialization-icon" />
              <h3>Group PPA</h3>
              <p style={{ textAlign: 'center' }}>Multiple mid-sized companies pool demand to negotiate a single PPA with a renewable energy developer. Each entity gets a volume allocation at negotiated rates. Reduces individual negotiation costs and improves bargaining power.</p>
              <ul style={{ textAlign: 'left', marginTop: '1rem', paddingLeft: '1.5rem' }}>
                <li>Volume aggregation improves tariff negotiations</li>
                <li>Lower transaction costs per participant</li>
                <li>Suitable for 1-10 MW scale operations</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Additional Solutions Section */}
      <section className="utility-project-types">
        <div className="container">
          <h2>Complementary Enterprise Solutions</h2>
          <div className="project-types-grid">
            <div className="project-type">
              <TbBattery className="specialization-icon" style={{ fontSize: '40px' }} />
              <h3>Hybrid Solutions with BESS</h3>
              <p>Integrated Battery Energy Storage Systems (BESS) combined with renewable energy generation to provide round-the-clock clean power, peak shaving, and enhanced grid stability for critical operations.</p>
              <ul>
                <li>24/7 renewable energy availability</li>
                <li>Peak demand management</li>
                <li>Enhanced grid stability</li>
                <li>Backup power capabilities</li>
              </ul>
            </div>
            <div className="project-type">
              <TbPlug className="specialization-icon" style={{ fontSize: '40px' }} />
              <h3>Hybrid Power Solutions</h3>
              <p>Intelligent combination of grid power, renewable energy, and backup systems to ensure uninterrupted operations while maximizing renewable energy utilization and cost optimization.</p>
              <ul>
                <li>Optimized grid-renewable mix</li>
                <li>Uninterrupted power supply</li>
                <li>Cost optimization strategies</li>
                <li>Flexible power management</li>
              </ul>
            </div>
            <div className="project-type">
              <TbChartLine className="specialization-icon" style={{ fontSize: '40px' }} />
              <h3>Energy Management & Monitoring</h3>
              <p>Advanced energy management systems with real-time monitoring, analytics, and optimization tools to track performance, identify efficiency opportunities, and ensure optimal energy utilization.</p>
              <ul>
                <li>Real-time energy monitoring</li>
                <li>Performance analytics</li>
                <li>Predictive maintenance</li>
                <li>Energy optimization insights</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Enterprise Solutions Section */}
      <section className="utility-capabilities">
        <div className="container">
          <h2>Why Choose Our Enterprise Power Solutions</h2>
          <div className="capabilities-content">
            <div className="capability">
              <TbShield className="specialization-icon" style={{ fontSize: '40px', marginBottom: '1rem' }} />
              <h3>Energy Security</h3>
              <p>Protect your enterprise from grid instability, tariff volatility, and power outages with reliable renewable energy infrastructure and smart power management systems.</p>
            </div>
            <div className="capability">
              <TbChartLine className="specialization-icon" style={{ fontSize: '40px', marginBottom: '1rem' }} />
              <h3>Cost Predictability</h3>
              <p>Long-term fixed energy costs provide financial stability and improved budgeting for your operations, reducing exposure to electricity price fluctuations.</p>
            </div>
            <div className="capability">
              <TbBuildingFactory className="specialization-icon" style={{ fontSize: '40px', marginBottom: '1rem' }} />
              <h3>Regulatory Expertise</h3>
              <p>Full compliance with Indian renewable energy regulations, open access policies, and grid codes. Expert handling of regulatory approvals and documentation.</p>
            </div>
            <div className="capability">
              <TbPlug className="specialization-icon" style={{ fontSize: '40px', marginBottom: '1rem' }} />
              <h3>Tailored Solutions</h3>
              <p>Customized power procurement models designed specifically for your industry, operational requirements, and strategic energy goals with comprehensive project management.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="utility-cta">
        <div className="container">
          <h2>Ready to Secure Your Enterprise Energy Future?</h2>
          <p>Partner with Lunar Electric to design and implement the ideal power procurement model for your enterprise. 
             From Group Captive arrangements to VPPA contracts, we'll help you achieve energy security, cost predictability, and sustainability goals.</p>
          <div className="cta-buttons">
            <Link to="/callback" className="btn-primary">
              Discuss Your Requirements
            </Link>
            <a href="tel:+919148857417" className="btn-secondary">
              Call Now
            </a>
          </div>
        </div>
      </section>

      </div>
      <Footer />
    </>
  );
}

export default UtilityScaleSolar;
